import { arrow, autoPlacement, computePosition, offset, shift } from '@floating-ui/dom';

const defaults = {
    trigger: 'click'
},
    NO_TRIGGERS = 'Manual';

function uniqueId() {
    return `tooltip-${Math.random().toString(36).substring(2)}`;
}

export class Tooltip {
    constructor($trigger, opts = {}) {
        if (!$trigger) {
            throw new Error('Tooltip requires an element as a parameter');
        }

        this.$trigger = $trigger;

        this.options = {
            ...defaults,
            ...opts
        };

        this.content = '';

        if (!this.$trigger.hasAttribute('tabindex')) {
            this.$trigger.setAttribute('tabindex', '0');
        }

        if (typeof this.options.content === 'object') {
            let contentElement, contentArray;

            if (this.options.content.innerHTML) {
                contentElement = this.options.content;
            } else {
                contentArray = [].slice.call(this.options.content);
                if (contentArray.length) {
                    contentElement = contentArray[0];
                }
            }
            this.content = contentElement ? contentElement.innerHTML : '';
        } else if (typeof this.options.content === 'string') {
            this.content = this.options.content;
        } else if (typeof this.options.content === 'undefined' && this.$trigger.title) {
            this.content = this.$trigger.title;
        }

        if (this.options.trigger !== NO_TRIGGERS) {
            let listeners = [
                ['blur', () => this.hide()],
                ['mouseenter', () => this.show()],
                ['mouseleave', () => this.hide()],
                [
                    'keydown',
                    e => {
                        const SPACE_BAR = 32,
                            ENTER = 13;
                        if (e.keyCode === SPACE_BAR || e.keyCode === ENTER) {
                            e.preventDefault();
                            this.toggle();
                        }
                    }
                ],
                [
                    'click', e => e.preventDefault()
                ]
            ];

            if (this.options.trigger === 'click') {
                //remove mouse enter / leave
                listeners.splice(1, 2);

                listeners.push([
                    'click',
                    e => {
                        e.preventDefault();
                        this.toggle();
                    }
                ]);
            }

            listeners.forEach(([event, listener]) => {
                this.$trigger.addEventListener(event, listener);

                document.addEventListener(listener, e => {
                    if (e.target !== this.$trigger) {
                        this.hide();
                    }
                });
            });
        }

        this._isShown = false;
    }

    toggle() {
        if (!this._isShown) {
            this.show();
        } else {
            this.hide();
        }
    }

    show() {
        if (!this.$tooltip) {
            this.build();
        }

        this.$tooltip.style.display = 'block';
        this.$tooltip.setAttribute('aria-hidden', 'false');
        this.update();
        this._isShown = true;
    }

    hide() {
        if (!this._isShown) {
            return;
        }

        this.$tooltip.style.removeProperty('display');
        this.$tooltip.setAttribute('aria-hidden', 'true');
        this._isShown = false;
    }

    update() {
        computePosition(this.$trigger, this.$tooltip, {
            middleware: [
                autoPlacement(),
                offset(10),
                shift({
                    padding: 8
                }),
                arrow({
                    element: this.$arrow
                })
            ]
        }).then(({ x, y, placement, middlewareData }) => {
            Object.assign(this.$tooltip.style, {
                left: `${x}px`,
                top: `${y}px`
            });

            // Accessing the data
            const { x: arrowX, y: arrowY } = middlewareData.arrow,
                staticSide = {
                    top: 'bottom',
                    right: 'left',
                    bottom: 'top',
                    left: 'right'
                }[placement.split('-')[0]];

            Object.assign(this.$arrow.style, {
                left: arrowX != null ? `${arrowX}px` : '',
                top: arrowY != null ? `${arrowY}px` : '',
                right: '',
                bottom: '',
                [staticSide]: `-${this.$arrow.clientWidth / 2}px`
            });
        });
    }

    build() {
        const id = uniqueId();
        this.$tooltip = document.createElement('div');
        this.$tooltip.id = id;
        this.$tooltip.setAttribute('role', 'tooltip');
        this.$tooltip.setAttribute('aria-hidden', 'true');
        this.$tooltip.classList.add('sp-tooltip');

        this.$content = document.createElement('div');
        this.$content.classList.add('sp-tooltip-inner');
        this.$content.innerHTML = this.content;

        this.$arrow = document.createElement('div');
        this.$arrow.classList.add('sp-tooltip-arrow');

        if (this.options.width) {
            this.$tooltip.style.width = `${this.options.width.toString().replace(/px/gi, '')}px`;
        }
        this.$tooltip.appendChild(this.$content);
        this.$tooltip.appendChild(this.$arrow);

        this.$trigger.setAttribute('aria-describedby', id);
        document.body.appendChild(this.$tooltip);
    }
}

export function tooltip($triggers, options) {
    var triggers = [].slice.call('length' in $triggers ? $triggers : [$triggers]);

    if (triggers.length) {
        triggers.forEach($trigger => new Tooltip($trigger, options));
    }
}
